/**
 * <ashtakavarga-grid> — Bhinnāṣṭakavarga for the seven grahas and their sum, the
 * Sarvāṣṭakavarga, rāśi by rāśi; with the śodhana piṇḍas beneath.
 *
 * ◆ The lagna's column is marked in words as well as by style, and the SAV row
 * carries the classical band (§12.4) as text — "strong" at 30 or more rekhās, "average"
 * from 25, "weak" below — because a colour alone is not a reading.
 *
 * ⚠ Without a birth time there is no lagna, so the lagna contributes no points and
 * every total is short of the classical figure. The component says so.
 *
 * Set `.kundali`.
 *
 * © Manish Jagdish Thatte
 */

import { BAV_TOTALS, SAV_TOTAL, ashtakavarga, savBand } from '../jyotish/ashtakavarga.js';
import { PHYSICAL_GRAHA_IDS, RASHIS, graha } from '../jyotish/constants.js';
import { h, nz, notice } from '../utils/dom.js';
import { localName } from '../utils/i18n.js';

export class AshtakavargaGrid extends HTMLElement {
  connectedCallback() { this.render(); }

  set kundali(value) { this._kundali = value; this.render(); }
  get kundali() { return this._kundali ?? null; }

  render() {
    this.textContent = '';
    const k = this._kundali;
    if (!k) return;

    const av = ashtakavarga(k);
    const lagna = k.lagna ? k.lagna.rashiIndex : null;

    if (!av.lagnaIncluded) {
      this.append(notice(
        'The birth time is unknown, so the lagna contributes no points. Every total below is short of ' +
        'the classical figure, and the Sarvāṣṭakavarga does not reach 337.', 'warning'));
    }

    const colHead = (r) => h('th', { scope: 'col', class: r.index === lagna ? 'is-lagna' : null },
      r.abbr, r.index === lagna ? h('span', { class: 'visually-hidden' }, ' (lagna)') : null);

    const head = h('tr', {}, h('th', { scope: 'col' }, 'Graha'), RASHIS.map(colHead), h('th', { scope: 'col' }, 'Total'));

    const body = PHYSICAL_GRAHA_IDS.map((id) => {
      const b = av.bhinna[id];
      return h('tr', {},
        h('th', { scope: 'row' }, localName(graha(id).name)),
        b.points.map((v, r) => h('td', { class: `is-numeric${r === lagna ? ' is-lagna' : ''}` }, nz(v))),
        h('td', { class: 'is-numeric' }, nz(b.total), av.lagnaIncluded ? h('span', { class: 'field-note' }, nz(` / ${BAV_TOTALS[id]}`)) : null));
    });

    const savRow = h('tr', { class: 'ashtakavarga-grid__sav' },
      h('th', { scope: 'row' }, 'Sarva'),
      av.sarva.points.map((v, r) => h('td', { class: `is-numeric is-${savBand(v)}${r === lagna ? ' is-lagna' : ''}` }, nz(v))),
      h('td', { class: 'is-numeric' }, nz(av.sarva.total), av.lagnaIncluded ? h('span', { class: 'field-note' }, nz(` / ${SAV_TOTAL}`)) : null));

    const bandRow = h('tr', { class: 'ashtakavarga-grid__band' },
      h('th', { scope: 'row' }, 'Band'),
      av.sarva.points.map((v) => h('td', {}, savBand(v))),
      h('td', {}));

    this.append(h('div', { class: 'table-scroll' },
      h('table', { class: 'data-table ashtakavarga-grid' },
        h('caption', {}, 'Rekhās in each rāśi — Bhinnāṣṭakavarga of each graha, and their sum'),
        h('thead', {}, head), h('tbody', {}, body, savRow, bandRow))));
    this.append(h('p', { class: 'field-note' },
      'Parāśara / BPHS (§12.4): a rāśi with 30 or more rekhās is strong, 25 to 30 average, fewer than 25 weak. ' +
      'The lagna is included as an eighth contributor.'));

    const pinda = h('table', { class: 'data-table' },
      h('caption', {}, 'Śodhana — after trikoṇa and ekādhipatya reduction'),
      h('thead', {}, h('tr', {}, ['Graha', 'Rāśi piṇḍa', 'Graha piṇḍa', 'Śodhya piṇḍa'].map((c) => h('th', { scope: 'col' }, c)))),
      h('tbody', {}, PHYSICAL_GRAHA_IDS.map((id) => {
        const s = av.sodhana[id];
        return h('tr', {}, h('th', { scope: 'row' }, localName(graha(id).name)),
          h('td', { class: 'is-numeric' }, nz(s.rashiPinda)),
          h('td', { class: 'is-numeric' }, nz(s.grahaPinda)),
          h('td', { class: 'is-numeric' }, nz(s.sodhyaPinda)));
      })));
    this.append(h('details', {}, h('summary', {}, 'Śodhana and piṇḍas'), h('div', { class: 'table-scroll' }, pinda)));
  }
}

customElements.define('ashtakavarga-grid', AshtakavargaGrid);
